import { useState, useEffect, useCallback } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Download, Upload, Clock, RotateCcw, Database, Trash2, Cloud, CloudOff, CheckCircle, Loader2, ExternalLink, ChevronDown, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import NotificationMailboxCard from "./NotificationMailboxCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
const OAUTH_ERRORS = {
  invalid_state: "sign-in link expired or was not started from this page. Click Connect again.",
  account_not_allowed: "that Microsoft account is not part of the company domain.",
};

const BackupTab = ({ backupHistory, getAuthHeaders, onRefresh, onDataRefresh }) => {
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  
  // OneDrive state
  const [driveStatus, setDriveStatus] = useState({ connected: false });
  const [driveBackups, setDriveBackups] = useState([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveBackupLoading, setDriveBackupLoading] = useState(false);
  const [driveRestoreLoading, setDriveRestoreLoading] = useState(null);
  const [driveRestoreDialogOpen, setDriveRestoreDialogOpen] = useState(false);
  const [driveBackupError, setDriveBackupError] = useState(null);
  const [selectedDriveBackup, setSelectedDriveBackup] = useState(null);

  // Photo storage health
  const [photoHealth, setPhotoHealth] = useState(null);
  const [photoHealthLoading, setPhotoHealthLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const fetchPhotoHealth = useCallback(async () => {
    setPhotoHealthLoading(true);
    try {
      const res = await apiClient.get(`/jobs/photos/storage-health`);
      setPhotoHealth(res.data);
    } catch (error) {
      console.error("Failed to fetch photo storage health", error);
    } finally {
      setPhotoHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhotoHealth();
  }, [fetchPhotoHealth]);

  const handleMigratePhotos = async () => {
    setMigrating(true);
    try {
      const res = await apiClient.post(`/jobs/photos/migrate-to-onedrive`);
      const d = res.data;
      toast.success(`${d.migrated} photo(s) migrated to OneDrive${d.failed ? `, ${d.failed} failed` : ""}${d.skipped_no_file ? `, ${d.skipped_no_file} had no local file` : ""}.`);
      fetchPhotoHealth();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to migrate photos");
    } finally {
      setMigrating(false);
    }
  };

  // Check for OAuth callback result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'onedrive_connected') {
      toast.success("OneDrive connected successfully!");
      window.history.replaceState({}, '', window.location.pathname);
      fetchDriveStatus();
    }
    if (params.get('error') && !params.get('error').startsWith('m365_mail_')) {
      toast.error(`OneDrive error: ${OAUTH_ERRORS[params.get('error')] || params.get('error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchDriveStatus = useCallback(async () => {
    try {
      const res = await apiClient.get(`/oauth/onedrive/status`);
      setDriveStatus(res.data);
      
      if (res.data.connected) {
        fetchDriveBackups();
      }
    } catch (error) {
      console.error("Failed to fetch OneDrive status", error);
    }
  }, [getAuthHeaders]);

  const fetchDriveBackups = async () => {
    setDriveLoading(true);
    setDriveBackupError(null);
    try {
      const res = await apiClient.get(`/oauth/onedrive/backups`);
      const backups = res.data.backups || [];
      // Only keep the most recent backup
      setDriveBackups(backups.length > 0 ? [backups[0]] : []);
    } catch (error) {
      console.error("Failed to fetch OneDrive backups", error);
      const msg = error.response?.data?.detail || error.message || "Unknown error";
      setDriveBackupError(msg);
      if (error.response?.status === 401) {
        setDriveStatus({ connected: false });
      }
    } finally {
      setDriveLoading(false);
    }
  };

  useEffect(() => {
    fetchDriveStatus();
  }, [fetchDriveStatus]);

  const handleConnectDrive = async () => {
    try {
      const res = await apiClient.get(`/oauth/onedrive/connect`);
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (error) {
      toast.error("Failed to connect OneDrive");
    }
  };

  const handleDisconnectDrive = async () => {
    try {
      await apiClient.post(`/oauth/onedrive/disconnect`);
      setDriveStatus({ connected: false });
      setDriveBackups([]);
      toast.success("OneDrive disconnected");
    } catch (error) {
      toast.error("Failed to disconnect OneDrive");
    }
  };

  const handleDriveBackup = async () => {
    setDriveBackupLoading(true);
    try {
      const res = await apiClient.post(`/oauth/onedrive/backup-now`);
      toast.success(`Backup uploaded to OneDrive: ${res.data.filename}`);
      fetchDriveBackups();
      onRefresh();
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      if (detail.includes("reconnect") || detail.includes("expired")) {
        setDriveStatus({ connected: false });
        toast.error("OneDrive connection expired. Please reconnect below.");
      } else {
        toast.error(detail || "Failed to backup to OneDrive");
      }
    } finally {
      setDriveBackupLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await apiClient.post(`/backup/create`, {}, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
        : `v2r_masonry_backup_${new Date().toISOString().split('T')[0]}.json`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded successfully");
      onRefresh();
    } catch (error) {
      toast.error("Failed to create backup");
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setRestoring(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await apiClient.post(`/backup/restore`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Backup restored successfully! Restored: ${Object.entries(response.data.restored_counts || {}).map(([k, v]) => `${v} ${k}`).join(', ')}`);
      setRestoreDialogOpen(false);
      onDataRefresh();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore backup");
    } finally {
      setRestoring(false);
      e.target.value = '';
    }
  };

  const handleDeleteRecord = async (id) => {
    try {
      await apiClient.delete(`/backup/history/${id}`);
      toast.success("Record deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  const handleRestoreFromDrive = async () => {
    if (!selectedDriveBackup) return;
    
    setDriveRestoreLoading(selectedDriveBackup.id);
    try {
      const response = await apiClient.post(
        `/oauth/onedrive/restore/${selectedDriveBackup.id}`);
      toast.success(`Backup restored from OneDrive! Restored: ${Object.entries(response.data.restored_counts || {}).map(([k, v]) => `${v} ${k}`).join(', ')}`);
      setDriveRestoreDialogOpen(false);
      setSelectedDriveBackup(null);
      onDataRefresh();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore from OneDrive");
    } finally {
      setDriveRestoreLoading(null);
    }
  };

  const openDriveRestoreDialog = (backup) => {
    setSelectedDriveBackup(backup);
    setDriveRestoreDialogOpen(true);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* OneDrive Cloud Backup */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-700">
            <Cloud className="w-5 h-5" />
            OneDrive Auto-Backup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {driveStatus.connected ? (
            <>
              <div className="flex items-center justify-between p-3 bg-green-100 text-green-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <span className="font-medium">Connected to OneDrive</span>
                    {driveStatus.user_email && (
                      <p className="text-xs text-green-700">{driveStatus.user_email}</p>
                    )}
                  </div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                      <CloudOff className="w-4 h-4 mr-1" />
                      Disconnect
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disconnect OneDrive?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Automatic backups will stop. Your existing backups in OneDrive will not be deleted.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDisconnectDrive} className="bg-destructive text-destructive-foreground">
                        Disconnect
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={handleDriveBackup}
                  disabled={driveBackupLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="drive-backup-now-btn"
                >
                  {driveBackupLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Backing up...
                    </span>
                  ) : (
                    <>
                      <Cloud className="w-4 h-4 mr-2" />
                      Backup to OneDrive Now
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={fetchDriveBackups}
                  disabled={driveLoading}
                  data-testid="refresh-drive-btn"
                >
                  <RotateCcw className={`w-4 h-4 mr-2 ${driveLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              <div className="text-sm text-muted-foreground p-3 bg-blue-100/50 rounded-lg">
                <p className="font-medium text-blue-800">Automatic Backup Schedule</p>
                <p>Monday - Friday at 5:30 PM UTC (11:30 AM CT)</p>
                <p className="text-xs mt-1">Backups are stored in your OneDrive folder: <strong>V2R Masonry Backups</strong></p>
              </div>

              {/* Most Recent OneDrive Backup */}
              {driveLoading ? (
                <div className="flex items-center gap-2 p-3 bg-white rounded border text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading latest backup...
                </div>
              ) : driveBackups.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-blue-800">Most Recent Backup</p>
                  {driveBackups.map((backup) => (
                    <div key={backup.id} className="flex items-center justify-between p-3 bg-white rounded-lg border text-sm">
                      <div className="flex items-center gap-3 truncate flex-1 min-w-0">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <div className="truncate">
                          <p className="font-medium truncate">{backup.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {backup.created_at ? new Date(backup.created_at).toLocaleString() : ""}
                            {backup.size ? ` - ${formatFileSize(backup.size)}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDriveRestoreDialog(backup)}
                          disabled={driveRestoreLoading === backup.id}
                          className="h-8 px-3 text-xs"
                          data-testid={`restore-drive-${backup.id}`}
                        >
                          {driveRestoreLoading === backup.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="w-3 h-3 mr-1" />
                              Restore
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">
                    Older backups are available directly in your OneDrive &gt; V2R Masonry Backups folder.
                  </p>
                </div>
              ) : driveBackupError ? (
                <div className="p-3 bg-red-50 rounded border border-red-200 text-sm text-red-700">
                  Failed to load backups: {driveBackupError}
                </div>
              ) : (
                <div className="p-3 bg-white rounded border text-sm text-muted-foreground">
                  No backups found in OneDrive yet. Click "Backup to OneDrive Now" to create one.
                </div>
              )}

              {/* Restore from OneDrive Confirmation Dialog */}
              <AlertDialog open={driveRestoreDialogOpen} onOpenChange={setDriveRestoreDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-destructive">Warning: Restore from OneDrive</AlertDialogTitle>
                    <AlertDialogDescription className="space-y-2">
                      <p><strong>Warning:</strong> This action will permanently replace all existing data with the backup data.</p>
                      <p>Restoring from: <strong>{selectedDriveBackup?.name}</strong></p>
                      <p>This includes: Jobs, Employees, Materials, Daily Entries, and Settings.</p>
                      <p className="text-destructive font-medium">This action cannot be undone.</p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={driveRestoreLoading}>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleRestoreFromDrive}
                      disabled={driveRestoreLoading}
                      className="bg-destructive text-destructive-foreground"
                    >
                      {driveRestoreLoading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Restoring...
                        </span>
                      ) : (
                        "Restore Backup"
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Connect your OneDrive for automatic daily backups (Mon-Fri at 5:30 PM UTC). Your backups will be stored securely in your own OneDrive account.
              </p>
              <Button 
                onClick={handleConnectDrive}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="connect-drive-btn"
              >
                <Cloud className="w-4 h-4 mr-2" />
                Connect OneDrive
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <NotificationMailboxCard />

      {/* Photo Storage Health */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Photo Storage Health
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {photoHealthLoading && !photoHealth ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking photo storage...
            </div>
          ) : photoHealth ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3 text-center" data-testid="photo-health-total">
                  <p className="text-2xl font-semibold">{photoHealth.total_photos}</p>
                  <p className="text-xs text-muted-foreground">Total photos</p>
                </div>
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-center" data-testid="photo-health-onedrive">
                  <p className="text-2xl font-semibold text-green-700">{photoHealth.onedrive_count}</p>
                  <p className="text-xs text-green-700">On OneDrive</p>
                </div>
                <div className={`rounded-lg border p-3 text-center ${photoHealth.at_risk_count > 0 ? "border-amber-300 bg-amber-50" : ""}`} data-testid="photo-health-local">
                  <p className={`text-2xl font-semibold ${photoHealth.at_risk_count > 0 ? "text-amber-700" : ""}`}>{photoHealth.local_count}</p>
                  <p className="text-xs text-muted-foreground">Local only</p>
                </div>
              </div>

              {!photoHealth.onedrive_connected ? (
                <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-sm text-muted-foreground">
                  <CloudOff className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>OneDrive is not connected, so photos are stored on the server only. Connect OneDrive above to store photos durably.</span>
                </div>
              ) : photoHealth.at_risk_count > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>
                      <strong>{photoHealth.at_risk_count} photo(s)</strong> are stored on the server only and not yet backed up to OneDrive
                      {photoHealth.local_missing_count > 0 && <> ({photoHealth.local_missing_count} of these no longer have a local file and can't be recovered)</>}.
                      Migrate them so they're safe.
                    </span>
                  </div>
                  <Button onClick={handleMigratePhotos} disabled={migrating} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="migrate-photos-btn">
                    {migrating ? (
                      <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Migrating photos...</span>
                    ) : (
                      <><Cloud className="w-4 h-4 mr-2" /> Migrate {photoHealth.at_risk_count} Photo(s) to OneDrive</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700">
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  All photos are safely stored on OneDrive.
                </div>
              )}

              <Button variant="ghost" size="sm" onClick={fetchPhotoHealth} disabled={photoHealthLoading} data-testid="refresh-photo-health-btn">
                <RotateCcw className={`w-4 h-4 mr-2 ${photoHealthLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Unable to load photo storage status.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Create Backup Card (Local) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Download Backup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Download a complete backup of all your data to your computer.
            </p>
            <Button 
              onClick={handleCreateBackup}
              disabled={backupLoading}
              className="w-full"
              data-testid="create-backup-btn"
            >
              {backupLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating Backup...
                </span>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Backup
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Restore Backup Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Restore Backup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Restore your data from a previously downloaded backup file. This will replace all current data.
            </p>
            <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="w-full" data-testid="restore-backup-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Restore from Backup
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-destructive">Warning: Restore Backup</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p><strong>Warning:</strong> This action will permanently replace all existing data with the backup data.</p>
                    <p>This includes: Jobs, Employees, Materials, and Settings.</p>
                    <p className="text-destructive font-medium">This action cannot be undone.</p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-4">
                  <Label htmlFor="backup-file" className="text-sm font-medium">Select Backup File</Label>
                  <Input 
                    id="backup-file"
                    type="file" 
                    accept=".json"
                    className="mt-2"
                    data-testid="backup-file-input"
                    onChange={handleRestore}
                    disabled={restoring}
                  />
                  {restoring && (
                    <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Restoring backup...
                    </p>
                  )}
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={restoring}>Cancel</AlertDialogCancel>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>

      {/* Backup History - Collapsible */}
      <Collapsible>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 p-0 h-auto hover:bg-transparent" data-testid="toggle-backup-history">
                <Clock className="w-5 h-5" />
                <span className="text-lg font-semibold">Backup History ({backupHistory.length})</span>
                <ChevronDown className="w-4 h-4 transition-transform duration-200 [&[data-state=open]]:rotate-180" />
              </Button>
            </CollapsibleTrigger>
            <Button variant="ghost" size="sm" onClick={onRefresh} data-testid="refresh-history-btn">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
              {backupHistory.length > 0 ? (
                <div className="space-y-3">
                  {backupHistory.map((backup) => (
                    <div key={backup.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {backup.type === "restore" ? (
                            <Upload className="w-4 h-4 text-amber-600" />
                          ) : backup.type?.includes("onedrive") || backup.type?.includes("google_drive") ? (
                            <Cloud className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Download className="w-4 h-4 text-green-600" />
                          )}
                          <span className="font-medium">
                            {backup.type === "restore" ? "Restored" : 
                             backup.type?.includes("onedrive") ? "OneDrive Backup" :
                             backup.type?.includes("google_drive") ? "Google Drive Backup" : "Backup Created"}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {new Date(backup.created_at || backup.timestamp).toLocaleString()}
                          </span>
                          {backup.type?.includes("scheduled") && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Auto</span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {backup.type === "restore" ? (
                            <>Restored by {backup.restored_by_name}</>
                          ) : (
                            <>
                              {backup.created_by_name && `Created by ${backup.created_by_name}`}
                              {backup.file_size && ` - ${formatFileSize(backup.file_size)}`}
                              {backup.size && ` - ${formatFileSize(backup.size)}`}
                              {backup.filename && <span className="text-xs ml-2">{backup.filename}</span>}
                            </>
                          )}
                        </div>
                        {(backup.records || backup.restored_counts) && (
                          <div className="text-xs text-muted-foreground">
                            Records: {Object.entries(backup.records || backup.restored_counts || {}).map(([k, v]) => `${v} ${k}`).join(', ')}
                          </div>
                        )}
                        {backup.status === "failed" && (
                          <div className="text-xs text-red-600">Failed: {backup.error}</div>
                        )}
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive" data-testid={`delete-backup-${backup.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Record?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will remove this backup record from history. Any downloaded backup files will not be affected.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteRecord(backup.id)}
                              className="bg-destructive text-destructive-foreground"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No backup history yet</p>
                  <p className="text-sm mt-1">Create your first backup to see it here</p>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default BackupTab;
